import type { BriefDocumentKind, CampaignBriefStatus } from "@/hooks/api/use-campaign-briefs";

export interface RegionOption {
	value: string;
	label: string;
	/** IANA zone. A time on a request is read in each region's own clock. */
	timezone: string;
	clock: string;
}

/** Keep in step with `BRIEF_REGIONS` in the API, which refuses any other code. */
export const REGIONS: RegionOption[] = [
	{ value: "SG", label: "Singapore", timezone: "Asia/Singapore", clock: "SGT" },
	{ value: "AU", label: "Australia", timezone: "Australia/Sydney", clock: "AEST" },
	{ value: "MY", label: "Malaysia", timezone: "Asia/Kuala_Lumpur", clock: "MYT" },
	{ value: "TH", label: "Thailand", timezone: "Asia/Bangkok", clock: "ICT" },
	{ value: "ID", label: "Indonesia", timezone: "Asia/Jakarta", clock: "WIB" },
	{ value: "JP", label: "Japan", timezone: "Asia/Tokyo", clock: "JST" },
];

/** BCP 47 tags. */
export const LANGUAGES: { value: string; label: string }[] = [
	{ value: "en", label: "English" },
	{ value: "ms-MY", label: "Bahasa Melayu" },
	{ value: "th", label: "Thai" },
	{ value: "id", label: "Bahasa Indonesia" },
	{ value: "zh-Hans-SG", label: "Mandarin (Simplified)" },
	{ value: "ta-SG", label: "Tamil" },
	{ value: "ja", label: "Japanese" },
];

export const regionLabel = (code: string) => REGIONS.find(({ value }) => value === code)?.label ?? code;
export const languageLabel = (tag: string) => LANGUAGES.find(({ value }) => value === tag)?.label ?? tag;

export const STATUS_LABELS: Record<CampaignBriefStatus, string> = {
	draft: "Draft",
	submitted: "Submitted",
	in_review: "In review",
	scheduled: "Scheduled",
	live: "Live",
	completed: "Completed",
	cancelled: "Cancelled",
};

export const STATUS_COLORS: Record<
	CampaignBriefStatus,
	"default" | "primary" | "info" | "warning" | "success" | "error" | "secondary"
> = {
	draft: "default",
	submitted: "primary",
	in_review: "info",
	scheduled: "warning",
	live: "success",
	completed: "secondary",
	cancelled: "error",
};

/** Mirrors `BRIEF_TRANSITIONS` in the API: where we can move a request from each status. */
export const NEXT_STATUSES: Record<CampaignBriefStatus, CampaignBriefStatus[]> = {
	draft: [],
	submitted: ["in_review", "scheduled", "cancelled"],
	in_review: ["scheduled", "cancelled"],
	scheduled: ["in_review", "live", "cancelled"],
	live: ["completed", "cancelled"],
	completed: [],
	cancelled: [],
};

export const DOCUMENTS: Record<BriefDocumentKind, { label: string; accept: string; hint: string }> = {
	call_flow: { label: "Call flow", accept: ".pdf,.docx,.txt", hint: "PDF, Word or text" },
	lead_list: { label: "Lead list", accept: ".csv", hint: "CSV only. Export Excel sheets as CSV UTF-8" },
	po: { label: "PO", accept: ".pdf,.doc,.docx", hint: "PDF or Word" },
	sow: { label: "SOW", accept: ".pdf,.doc,.docx", hint: "PDF or Word" },
};

/** The API's per-file ceiling. Checked here too, so a big file fails before it uploads. */
export const MAX_FILE_BYTES = 25 * 1024 * 1024;

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Weekly hours in a line a person reads, consecutive days with the same hours folded together. */
export function describeSchedule(schedule: { dow: number; from: string; to: string }[]): string {
	const hoursOn = (dow: number) =>
		schedule
			.filter((window) => window.dow === dow)
			.toSorted((a, b) => a.from.localeCompare(b.from))
			.map(({ from, to }) => `${from}–${to}`)
			.join(", ");

	const groups: { first: number; last: number; hours: string }[] = [];
	for (let dow = 1; dow <= 7; dow++) {
		const hours = hoursOn(dow);
		if (!hours) continue;
		const previous = groups.at(-1);
		if (previous && previous.last === dow - 1 && previous.hours === hours) previous.last = dow;
		else groups.push({ first: dow, last: dow, hours });
	}

	return groups
		.map(({ first, last, hours }) =>
			first === last ? `${DAY_NAMES[first - 1]} ${hours}` : `${DAY_NAMES[first - 1]}–${DAY_NAMES[last - 1]} ${hours}`
		)
		.join("; ");
}

export const formatBytes = (bytes: number) =>
	bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
